import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  PlayArrow as PlayArrowIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import logger from '../utils/logger';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const FilePreview = ({ open, onClose, previewData, onImport, loading }) => {
  const { user, isSupervisor } = useAuth();
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [selectAllRows, setSelectAllRows] = useState(true);
  const [selectAllColumns, setSelectAllColumns] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [recordSearchTerm, setRecordSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [claimForUser, setClaimForUser] = useState('');
  const [enableClaiming, setEnableClaiming] = useState(true); // Always enabled, user must be selected
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [validating, setValidating] = useState(false);
  const [showValidationResults, setShowValidationResults] = useState(false);
  const [validationRun, setValidationRun] = useState(false); // Track if validation has been run
  const [filter1, setFilter1] = useState('');
  const [filter2, setFilter2] = useState('');
  const [filter3, setFilter3] = useState('');
  const [filteredDataFromServer, setFilteredDataFromServer] = useState(null);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [filterTimeout, setFilterTimeout] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importErrorDetails, setImportErrorDetails] = useState(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const {
    preview_id,
    data = [],
    columns = [],
    total_rows = 0,
    preview_rows = 0,
    columns_info = {},
    original_filename = '',
    alignment_warnings = []
  } = previewData || {};

  // Filter columns based on search
  const filteredColumns = useMemo(() => {
    if (!searchTerm) return columns;
    const term = searchTerm.toLowerCase();
    return columns.filter(col => col.toLowerCase().includes(term));
  }, [columns, searchTerm]);

  // Filter data based on Filter1, Filter2, Filter3
  // If filters are active, use server-side filtered data, otherwise use local data
  const filteredData = useMemo(() => {
    let baseData;
    if (filteredDataFromServer !== null) {
      baseData = filteredDataFromServer;
    } else {
      baseData = data;
    }
    
    // Apply record search filter if search term is provided
    if (!recordSearchTerm.trim()) {
      return baseData;
    }
    
    const searchLower = recordSearchTerm.toLowerCase();
    return baseData.filter(row => {
      // Search through all column values in the row
      return Object.values(row).some(value => {
        if (value === null || value === undefined) return false;
        const strValue = String(value).toLowerCase();
        return strValue.includes(searchLower);
      });
    });
  }, [filteredDataFromServer, data, recordSearchTerm]);

  // Fetch filtered data from server when filters change
  React.useEffect(() => {
    // Clear existing timeout
    if (filterTimeout) {
      clearTimeout(filterTimeout);
    }

    // If no filters are active, use original data
    if (!filter1 && !filter2 && !filter3) {
      setFilteredDataFromServer(null);
      return;
    }

    // Debounce API call
    const timeout = setTimeout(async () => {
      if (!preview_id) return;
      
      setLoadingFilters(true);
      try {
        const response = await api.post('/api/files/preview-filter', {
          preview_id,
          filter1: filter1 || '',
          filter2: filter2 || '',
          filter3: filter3 || ''
        });
        
        if (response.data.success) {
          setFilteredDataFromServer(response.data.data);
          logger.info(`Loaded ${response.data.filtered_rows} filtered rows from server`);
        } else {
          logger.error('Error fetching filtered data:', response.data.message);
          setFilteredDataFromServer(null);
        }
      } catch (error) {
        logger.error('Error fetching filtered data:', error);
        setFilteredDataFromServer(null);
      } finally {
        setLoadingFilters(false);
      }
    }, 500); // 500ms debounce

    setFilterTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [filter1, filter2, filter3, preview_id]);

  // Get indices of filtered rows in the original data array
  // This includes both Filter1/Filter2/Filter3 filters AND recordSearchTerm filter
  const filteredRowIndices = useMemo(() => {
    // First, get base filtered indices from Filter1/Filter2/Filter3
    let baseFilteredIndices = null;
    if (filter1 || filter2 || filter3) {
      if (filteredDataFromServer === null) {
        return null; // Still loading server data
      }
      // Extract original indices from server-filtered data
      baseFilteredIndices = filteredDataFromServer
        .map(row => row._original_index)
        .filter(idx => idx !== undefined && idx !== null);
    }
    
    // Then apply recordSearchTerm filter if active
    if (recordSearchTerm.trim()) {
      // Get indices from filteredData (which already includes recordSearchTerm filter)
      const searchFilteredIndices = filteredData
        .map(row => {
          // If we have _original_index from server filtering, use that
          if (row._original_index !== undefined) {
            return row._original_index;
          }
          // Otherwise, find the index in the original data
          const idx = data.findIndex(r => r === row);
          return idx >= 0 ? idx : null;
        })
        .filter(idx => idx !== null && idx !== undefined);
      
      // If baseFilteredIndices exists, intersect with searchFilteredIndices
      if (baseFilteredIndices !== null) {
        const baseSet = new Set(baseFilteredIndices);
        return searchFilteredIndices.filter(idx => baseSet.has(idx));
      }
      
      return searchFilteredIndices.length > 0 ? searchFilteredIndices : [];
    }
    
    // Only Filter1/Filter2/Filter3 filters are active
    return baseFilteredIndices;
  }, [filter1, filter2, filter3, filteredDataFromServer, recordSearchTerm, filteredData, data]);

  // Initialize selections when data changes
  React.useEffect(() => {
    if (open && data.length > 0) {
      // Select all rows by default
      setSelectedRows(new Set(data.map((_, idx) => idx)));
      setSelectAllRows(true);
      // Select all columns by default
      setSelectedColumns(new Set(columns));
      setSelectAllColumns(true);
      setPage(0);
      setSearchTerm('');
      setRecordSearchTerm('');
      setFilter1('');
      setFilter2('');
      setFilter3('');
      setFilteredDataFromServer(null);
      setValidationResults(null);
      setValidationRun(false); // Reset validation when preview data changes
      setShowValidationResults(false);
      // claimForUser will be set to logged-in user when users are loaded
    }
  }, [open, data, columns]);

  // Update selection when filters change - only keep selections that are still in filtered data
  React.useEffect(() => {
    // Check if any filters are active (Filter1/Filter2/Filter3 or recordSearchTerm)
    const hasFilters = (filter1 || filter2 || filter3) || (recordSearchTerm && recordSearchTerm.trim());
    
    if (filteredRowIndices !== null && filteredRowIndices.length > 0) {
      // Filters are active - only keep selected rows that are in the filtered set
      setSelectedRows(prevSelected => {
        const newSelected = new Set();
        prevSelected.forEach(idx => {
          if (filteredRowIndices.includes(idx)) {
            newSelected.add(idx);
          }
        });
        
        // Update selectAllRows based on whether all filtered rows are selected
        const allFilteredSelected = newSelected.size === filteredRowIndices.length && filteredRowIndices.length > 0;
        setSelectAllRows(allFilteredSelected);
        
        return newSelected;
      });
    } else if (filteredRowIndices === null && !hasFilters) {
      // Filters were cleared - if no rows are selected, select all rows
      setSelectedRows(prevSelected => {
        if (prevSelected.size === 0) {
          setSelectAllRows(true);
          return new Set(data.map((_, idx) => idx));
        }
        return prevSelected;
      });
    }
  }, [filteredRowIndices, filter1, filter2, filter3, recordSearchTerm, data]);

  // Fetch users for claiming - always load when dialog opens
  React.useEffect(() => {
    if (open) {
      const fetchUsers = async () => {
        try {
          setLoadingUsers(true);
          
          if (isSupervisor && !user?.is_admin) {
            // For supervisors, fetch only supervised users + themselves
            const [assignedUsersResponse, currentUserResponse] = await Promise.all([
              api.get('/api/supervisor/assigned-users'),
              api.get('/api/admin/users') // Get current user info
            ]);
            
            const supervisedUsers = assignedUsersResponse.data.success 
              ? assignedUsersResponse.data.users 
              : [];
            
            // Get current user info
            const allUsers = currentUserResponse.data.success 
              ? currentUserResponse.data.users 
              : [];
            const currentUserInfo = allUsers.find(u => u.username === user?.username);
            
            // Combine supervised users with current user
            const allowedUsers = [];
            if (currentUserInfo) {
              allowedUsers.push(currentUserInfo);
            }
            supervisedUsers.forEach(supervisedUser => {
              // Avoid duplicates
              if (!allowedUsers.find(u => u.username === supervisedUser.username)) {
                allowedUsers.push(supervisedUser);
              }
            });
            
            setUsers(allowedUsers);
            
            // Always set to current user by default
            if (currentUserInfo) {
              setClaimForUser(currentUserInfo.username);
            }
          } else {
            // For admins, fetch all users
            const response = await api.get('/api/admin/users');
            if (response.data.success) {
              const allUsers = response.data.users || [];
              setUsers(allUsers);
              
              // Always set to current user by default
              const currentUserInfo = allUsers.find(u => u.username === user?.username);
              if (currentUserInfo) {
                setClaimForUser(currentUserInfo.username);
              }
            }
          }
        } catch (error) {
          logger.error('Error fetching users:', error);
        } finally {
            setLoadingUsers(false);
        }
      };
      fetchUsers();
    }
  }, [open, isSupervisor, user?.is_admin, user?.username]);

  const handleSelectAllRows = () => {
    if (selectAllRows) {
      setSelectedRows(new Set());
      setSelectAllRows(false);
    } else {
      // If filters are active, select only filtered rows
      if (filteredRowIndices !== null) {
        setSelectedRows(new Set(filteredRowIndices));
        setSelectAllRows(true);
      } else {
        setSelectedRows(new Set(data.map((_, idx) => idx)));
        setSelectAllRows(true);
      }
    }
    // Reset validation when selection changes
    setValidationRun(false);
    setValidationResults(null);
    setShowValidationResults(false);
  };

  const handleSelectRow = (index) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
    
    // Update selectAllRows based on current filter state
    const hasFilters = (filter1 || filter2 || filter3) || (recordSearchTerm && recordSearchTerm.trim());
    
    if (hasFilters && filteredRowIndices !== null) {
      // Filters are active - check if all filtered rows are selected
      const allFilteredSelected = filteredRowIndices.length > 0 && 
        filteredRowIndices.every(idx => newSelected.has(idx));
      setSelectAllRows(allFilteredSelected);
    } else {
      // No filters - check if all rows are selected
      setSelectAllRows(newSelected.size === data.length);
    }
    
    // Reset validation when selection changes
    setValidationRun(false);
    setValidationResults(null);
    setShowValidationResults(false);
  };

  const handleSelectAllColumns = () => {
    if (selectAllColumns) {
      setSelectedColumns(new Set());
      setSelectAllColumns(false);
    } else {
      setSelectedColumns(new Set(columns));
      setSelectAllColumns(true);
    }
    // Reset validation when selection changes
    setValidationRun(false);
    setValidationResults(null);
    setShowValidationResults(false);
  };

  const handleSelectColumn = (column) => {
    const newSelected = new Set(selectedColumns);
    if (newSelected.has(column)) {
      newSelected.delete(column);
    } else {
      newSelected.add(column);
    }
    setSelectedColumns(newSelected);
    setSelectAllColumns(newSelected.size === columns.length);
    
    // Reset validation when selection changes
    setValidationRun(false);
    setValidationResults(null);
    setShowValidationResults(false);
  };

  // Validation functions
  const validateEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validatePhone = (phone) => {
    if (!phone || typeof phone !== 'string') return false;
    // Remove spaces, dashes, parentheses for validation
    const cleaned = phone.replace(/[\s\-\(\)]/g, '');
    // Check if it's a valid phone number (at least 10 digits, may start with +)
    return /^(\+?\d{10,15})$/.test(cleaned);
  };

  const validateURL = (url) => {
    if (!url || typeof url !== 'string') return false;
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
      return urlObj.hostname.length > 0;
    } catch {
      return false;
    }
  };

  const validateLinkedIn = (value) => {
    if (!value || typeof value !== 'string') return false;
    const linkedInRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/.+/i;
    return linkedInRegex.test(value.trim());
  };

  const validatePostcode = (postcode) => {
    if (!postcode || typeof postcode !== 'string') return false;
    // Dutch postcode format: 1234AB (4 digits, 2 letters)
    const cleaned = postcode.replace(/\s/g, '').toUpperCase();
    return /^\d{4}[A-Z]{2}$/.test(cleaned);
  };

  const validateNumeric = (value, fieldName) => {
    if (!value && value !== 0) return { valid: true }; // Empty is OK for optional fields
    const strValue = String(value).trim();
    
    // Check for phone numbers in numeric fields - more comprehensive check
    const phoneIndicators = [
      strValue.includes('-'),
      strValue.includes(' '),
      strValue.includes('('),
      strValue.includes(')'),
      strValue.startsWith('+'),
      /^\d{2,3}-\d{6,}$/.test(strValue), // Pattern like 055-3035810
      /^\d{2,3}\s\d{6,}$/.test(strValue), // Pattern like 055 3035810
      /^0\d{1,2}-\d{6,}$/.test(strValue) // Dutch phone pattern
    ];
    
    if (phoneIndicators.some(indicator => indicator === true)) {
      return { valid: false, error: 'Bevat telefoonnummer in plaats van numerieke waarde' };
    }
    
    // Try to parse as number
    const numValue = parseFloat(strValue.replace(',', '.'));
    if (isNaN(numValue)) {
      return { valid: false, error: 'Geen geldig nummer' };
    }
    
    return { valid: true };
  };

  const runValidation = () => {
    setValidating(true);
    setShowValidationResults(true);
    
    // Determine which rows to validate based on filters and selection
    let rowsToValidate;
    if (filteredRowIndices !== null) {
      // Filters are active - only validate filtered rows
      if (selectAllRows) {
        rowsToValidate = filteredRowIndices;
      } else {
        rowsToValidate = filteredRowIndices.filter(idx => selectedRows.has(idx));
      }
    } else {
      // No filters - validate all or selected rows
      if (selectAllRows) {
        rowsToValidate = data.map((_, idx) => idx);
      } else {
        rowsToValidate = Array.from(selectedRows);
      }
    }
    
    const results = {
      totalRows: rowsToValidate.length,
      errors: [],
      warnings: [],
      summary: {
        totalErrors: 0,
        totalWarnings: 0,
        rowsWithErrors: new Set(),
        rowsWithWarnings: new Set()
      }
    };

    // Get the data to validate - use filteredData if filters are active, otherwise use original data
    let dataToValidate;
    if (filteredRowIndices !== null && filteredDataFromServer !== null) {
      // Filters are active - use filtered data
      if (selectAllRows) {
        dataToValidate = filteredDataFromServer;
      } else {
        // Only validate selected rows from filtered data
        const selectedIndices = new Set(selectedRows);
        dataToValidate = filteredDataFromServer.filter(row => 
          selectedIndices.has(row._original_index)
        );
      }
    } else {
      // No filters - use original data
      dataToValidate = rowsToValidate.map(idx => data[idx]).filter(row => row !== undefined);
    }
    
    const columnsToValidate = selectAllColumns 
      ? columns 
      : columns.filter(col => selectedColumns.has(col));

    // Define field validations
    const fieldValidations = {
      'Telefoonnummer': { validator: validatePhone, required: false, type: 'phone' },
      'Telefoonnummer Mobiel': { validator: validatePhone, required: false, type: 'phone' },
      'Telefoonnummer_Mobiel': { validator: validatePhone, required: false, type: 'phone' },
      'URL': { validator: validateURL, required: false, type: 'url' },
      'LinkedIn Link': { validator: validateLinkedIn, required: false, type: 'linkedin' },
      'LinkedIn_Link': { validator: validateLinkedIn, required: false, type: 'linkedin' },
      'Vestigingsadres Postcode': { validator: validatePostcode, required: false, type: 'postcode' },
      'Vestigingsadres_Postcode': { validator: validatePostcode, required: false, type: 'postcode' },
      'Correspondentieadres Postcode': { validator: validatePostcode, required: false, type: 'postcode' },
      'Correspondentieadres_Postcode': { validator: validatePostcode, required: false, type: 'postcode' },
      'Kamer van Koophandel nummer': { validator: (v) => validateNumeric(v, 'KvK'), required: false, type: 'numeric' },
      'Kamer_van_Koophandel_nummer': { validator: (v) => validateNumeric(v, 'KvK'), required: false, type: 'numeric' },
      'Vestigingsnummer': { validator: (v) => validateNumeric(v, 'Vestigingsnummer'), required: false, type: 'numeric' },
      'Vestigingsadres Huisnummer': { validator: (v) => validateNumeric(v, 'Huisnummer'), required: false, type: 'numeric' },
      'Vestigingsadres_Huisnummer': { validator: (v) => validateNumeric(v, 'Huisnummer'), required: false, type: 'numeric' },
      'SBI-code locatie': { validator: (v) => validateNumeric(v, 'SBI'), required: false, type: 'numeric' },
      'SBI_code_locatie': { validator: (v) => validateNumeric(v, 'SBI'), required: false, type: 'numeric' },
      'SBI-code 2-cijferig Locatie': { validator: (v) => validateNumeric(v, 'SBI'), required: false, type: 'numeric' },
      'SBI_code_2_cijferig_Locatie': { validator: (v) => validateNumeric(v, 'SBI'), required: false, type: 'numeric' }
    };

    // Check for email-like fields
    // Use specific patterns to avoid false positives like "Mailstop", "Mailbox", "Mailing"
    const emailFields = columnsToValidate.filter(col => {
      const lower = col.toLowerCase();
      return (
        lower.includes('email') ||
        lower.includes('e-mail') ||
        lower.includes('e_mail') ||
        lower.includes('e mail') ||
        lower.includes('mailadres') ||
        lower.includes('mailaddress')
      );
    });

    // Validate each row
    dataToValidate.forEach((row, rowIndex) => {
      // Get the actual row index - use _original_index if available (from filtered data), otherwise use the index from rowsToValidate
      let actualRowIndex;
      if (row._original_index !== undefined) {
        actualRowIndex = row._original_index;
      } else if (filteredRowIndices !== null) {
        actualRowIndex = filteredRowIndices[rowIndex];
      } else {
        actualRowIndex = rowsToValidate[rowIndex];
      }
      
      // Skip if row is undefined or null
      if (!row) {
        return;
      }
      
      // Validate email fields
      emailFields.forEach(field => {
        const value = row[field];
        if (value && !validateEmail(value)) {
          results.errors.push({
            row: actualRowIndex + 1,
            field,
            value,
            message: 'Ongeldig e-mailadres formaat'
          });
          results.summary.rowsWithErrors.add(actualRowIndex);
        }
      });

      // Detect numeric fields dynamically based on column names and types
      const numericFieldPatterns = [
        /huisnummer/i,
        /nummer/i,
        /code/i,
        /kvk/i,
        /kamer.*koophandel/i,
        /vestigingsnummer/i,
        /sbi/i
      ];

      // Validate known fields
      columnsToValidate.forEach(field => {
        const value = row[field];
        
        if (fieldValidations[field]) {
          const validation = fieldValidations[field];
          
          if (validation.required && !value) {
            results.errors.push({
              row: actualRowIndex + 1,
              field,
              value: '',
              message: 'Verplicht veld is leeg'
            });
            results.summary.rowsWithErrors.add(actualRowIndex);
          } else if (value) {
            if (validation.type === 'numeric') {
              const numResult = validation.validator(value);
              if (!numResult.valid) {
                results.errors.push({
                  row: actualRowIndex + 1,
                  field,
                  value,
                  message: numResult.error
                });
                results.summary.rowsWithErrors.add(actualRowIndex);
              }
            } else if (!validation.validator(value)) {
              results.errors.push({
                row: actualRowIndex + 1,
                field,
                value,
                message: `Ongeldig ${validation.type} formaat`
              });
              results.summary.rowsWithErrors.add(actualRowIndex);
            }
          }
        } else {
          // Check if field might be numeric based on name patterns
          // Exclude description fields (fields containing "Omschrijving") as they are text fields
          const isDescriptionField = /omschrijving/i.test(field);
          // Exclude phone/mobile fields - "Telefoonnummer" contains "nummer" so it would
          // otherwise incorrectly match the numeric patterns below.
          const isPhoneField = /telefoon|phone|mobiel|mobile|gsm|fax/i.test(field);
          const mightBeNumeric = !isDescriptionField && !isPhoneField && numericFieldPatterns.some(pattern => pattern.test(field));
          
          if (mightBeNumeric && value) {
            // Check if value looks like a phone number
            const strValue = String(value).trim();
            
            // Check for URLs first
            const looksLikeUrl = (
              strValue.toLowerCase().startsWith('http://') ||
              strValue.toLowerCase().startsWith('https://') ||
              strValue.toLowerCase().startsWith('www.')
            );
            
            if (looksLikeUrl) {
              results.errors.push({
                row: actualRowIndex + 1,
                field,
                value,
                message: 'URL gevonden in numeriek veld (wordt verplaatst naar URL veld of verwijderd)'
              });
              results.summary.rowsWithErrors.add(actualRowIndex);
            }
            
            const looksLikePhone = (
              strValue.includes('-') || 
              strValue.includes(' ') || 
              strValue.includes('(') || 
              strValue.includes(')') || 
              strValue.startsWith('+') ||
              /^\d{2,3}-\d{6,}$/.test(strValue) ||
              /^\d{2,3}\s\d{6,}$/.test(strValue) ||
              /^0\d{1,2}-\d{6,}$/.test(strValue)
            );
            
            if (looksLikePhone) {
              results.errors.push({
                row: actualRowIndex + 1,
                field,
                value,
                message: 'Telefoonnummer gevonden in numeriek veld (wordt geconverteerd of verplaatst naar Telefoonnummer veld)'
              });
              results.summary.rowsWithErrors.add(actualRowIndex);
            }
            
            // Check if value contains text/letters when it should be numeric
            // This catches cases like "Laan van Spitsbergen 2" in a numeric field
            const containsLetters = /[a-zA-Z]/.test(strValue);
            const containsMultipleWords = strValue.split(/\s+/).length > 1;
            
            if (containsLetters && (containsMultipleWords || strValue.length > 10)) {
              // Likely an address or text that shouldn't be in a numeric field
              results.errors.push({
                row: actualRowIndex + 1,
                field,
                value,
                message: `Tekst/adres gevonden in numeriek veld: "${strValue}". Dit veld moet alleen cijfers bevatten.`
              });
              results.summary.rowsWithErrors.add(actualRowIndex);
            } else if (containsLetters && !looksLikePhone && !looksLikeUrl) {
              // Single word with letters - might be a postcode or other mixed format
              // Check if it's a valid postcode format (Dutch: 1234AB)
              const isPostcode = /^\d{4}[A-Z]{2}$/i.test(strValue.replace(/\s/g, ''));
              if (!isPostcode) {
                results.errors.push({
                  row: actualRowIndex + 1,
                  field,
                  value,
                  message: `Tekst gevonden in numeriek veld: "${strValue}". Dit veld moet alleen cijfers bevatten.`
                });
                results.summary.rowsWithErrors.add(actualRowIndex);
              }
            }
          }
          
          // Check phone fields for wrong data types
          if ((field.toLowerCase().includes('telefoon') || field.toLowerCase().includes('phone')) && value) {
            const strValue = String(value).trim();
            
            // Check for URLs in phone fields
            const looksLikeUrl = (
              strValue.toLowerCase().startsWith('http://') ||
              strValue.toLowerCase().startsWith('https://') ||
              strValue.toLowerCase().startsWith('www.')
            );
            
            if (looksLikeUrl) {
              results.errors.push({
                row: actualRowIndex + 1,
                field,
                value,
                message: 'URL gevonden in telefoonnummer veld (wordt verplaatst naar URL veld)'
              });
              results.summary.rowsWithErrors.add(actualRowIndex);
            }
            
            // Check for names/text in phone fields
            const looksLikeName = (
              (strValue.includes(' ') && !/\d/.test(strValue)) ||
              /\b(bv|b\.v\.|nv|n\.v\.|ltd|inc|gmbh)\b/i.test(strValue)
            );
            
            if (looksLikeName) {
              results.errors.push({
                row: actualRowIndex + 1,
                field,
                value,
                message: 'Naam/tekst gevonden in telefoonnummer veld'
              });
              results.summary.rowsWithErrors.add(actualRowIndex);
            }
          }
        }
      });

      // Check for empty important fields (warnings)
      const importantFields = ['Instellingsnaam', 'Vestigingsadres', 'Vestigingsadres Plaats', 'Telefoonnummer'];
      importantFields.forEach(field => {
        if (columnsToValidate.includes(field) && !row[field]) {
          results.warnings.push({
            row: actualRowIndex + 1,
            field,
            message: 'Belangrijk veld is leeg'
          });
          results.summary.rowsWithWarnings.add(actualRowIndex);
        }
      });
    });

    results.summary.totalErrors = results.errors.length;
    results.summary.totalWarnings = results.warnings.length;

    setValidationResults(results);
    setValidating(false);
    setValidationRun(true); // Mark validation as run
    setShowValidationResults(true); // Show validation results
  };

  const handleImport = async () => {
    setImportError(null);
    setImportErrorDetails(null);
    setShowErrorDialog(false);
    
    // Check if validation has been run
    if (!validationRun) {
      setImportError('Validatie moet eerst worden uitgevoerd voordat geïmporteerd kan worden. Klik op "Test Run" om de data te valideren.');
      setShowErrorDialog(true);
      return;
    }
    
    // Check if validation has errors
    if (validationResults && validationResults.summary.totalErrors > 0) {
      setImportError(`Import is niet mogelijk omdat er ${validationResults.summary.totalErrors} validatiefout(en) zijn gevonden. Los eerst alle fouten op voordat je importeert.`);
      setShowErrorDialog(true);
      return;
    }
    
    // Determine which rows to import based on filters and selection
    let rowsToImport;
    if (filteredRowIndices !== null) {
      // Filters are active - only import filtered rows
      if (selectAllRows) {
        rowsToImport = filteredRowIndices;
      } else {
        // Map selectedRows (display indices) to original indices
        // selectedRows contains indices from the filtered display, we need to map them to original indices
        const selectedOriginalIndices = new Set();
        
        if (filteredDataFromServer !== null) {
          // Create a mapping from display index to original index
          filteredDataFromServer.forEach((row, displayIdx) => {
            if (selectedRows.has(displayIdx) && row._original_index !== undefined) {
              selectedOriginalIndices.add(row._original_index);
            }
          });
        }
        
        // Only import rows that are both in filtered set AND selected
        rowsToImport = filteredRowIndices.filter(idx => selectedOriginalIndices.has(idx));
      }
    } else {
      // No filters - import all or selected rows
      if (selectAllRows) {
        rowsToImport = null; // null means import all rows
      } else {
        rowsToImport = Array.from(selectedRows);
      }
    }
    
    // Convert Sets to Arrays for API
    const rowsArray = rowsToImport;
    const columnsArray = selectAllColumns ? null : Array.from(selectedColumns);

    try {
      // User selection is always required - use selected user or fallback to current user
      let finalClaimForUser = claimForUser || user?.username;
      if (!finalClaimForUser) {
        throw new Error('Geen gebruiker geselecteerd. Selecteer een gebruiker om door te gaan.');
      }
      
      await onImport({
        preview_id,
        selected_rows: rowsArray,
        selected_columns: columnsArray,
        claim_for_user: finalClaimForUser
      });
    } catch (error) {
      logger.error('Import error:', error);
      let errorMessage = 'Er is een fout opgetreden bij het importeren van de data.';
      let errorDetails = null;
      
      if (error?.response?.data?.message) {
        const backendMessage = error.response.data.message;
        errorDetails = {
          problematic_record: error.response.data.problematic_record,
          problematic_field: error.response.data.problematic_field,
          problematic_value: error.response.data.problematic_value
        };
        
        // Make error messages more user-friendly
        if (backendMessage.includes('invalid input syntax for type integer')) {
          errorMessage = 'Er zijn gegevens gevonden die niet in het juiste formaat zijn. ' +
            'Controleer of numerieke velden (zoals huisnummer, postcode) alleen cijfers bevatten en geen tekst. ' +
            'Gebruik de "Test Run" functie om deze fouten te vinden voordat je importeert.';
        } else if (backendMessage.includes('invalid input syntax')) {
          errorMessage = 'Er zijn gegevens gevonden die niet in het juiste formaat zijn. ' +
            'Controleer de data en gebruik de "Test Run" functie om fouten te vinden.';
        } else if (backendMessage.includes('duplicate key')) {
          errorMessage = 'Er zijn duplicaten gevonden in de data. ' +
            'Controleer of er geen dubbele records worden geïmporteerd.';
        } else if (backendMessage.includes('constraint')) {
          errorMessage = 'Er zijn gegevens die niet voldoen aan de vereisten. ' +
            'Controleer de data en gebruik de "Test Run" functie om fouten te vinden.';
        } else {
          errorMessage = backendMessage;
        }
      } else if (error?.response?.statusText) {
        errorMessage = `Import mislukt: ${error.response.statusText}`;
      } else if (error?.message) {
        errorMessage = `Import mislukt: ${error.message}`;
      }
      
      setImportError(errorMessage);
      setImportErrorDetails(errorDetails);
      setShowErrorDialog(true);
    }
  };

  const handleClose = () => {
    setSelectedRows(new Set());
    setSelectedColumns(new Set());
    setSelectAllRows(true);
    setSelectAllColumns(true);
    setSearchTerm('');
    setRecordSearchTerm('');
    setPage(0);
    setClaimForUser('');
    setEnableClaiming(false);
    setValidationResults(null);
    setShowValidationResults(false);
    setValidationRun(false); // Reset validation run status
    setFilter1('');
    setFilter2('');
    setFilter3('');
    setFilteredDataFromServer(null);
    if (filterTimeout) {
      clearTimeout(filterTimeout);
      setFilterTimeout(null);
    }
    onClose();
  };

  // Paginated data (using filtered data)
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, page, rowsPerPage]);

  // Reset page when filters or search change
  React.useEffect(() => {
    setPage(0);
  }, [filter1, filter2, filter3, recordSearchTerm]);

  // Calculate selected rows count based on filters
  const selectedRowsCount = useMemo(() => {
    if (filteredRowIndices !== null) {
      // Filters are active (Filter1/Filter2/Filter3 or recordSearchTerm)
      if (selectAllRows) {
        return filteredRowIndices.length;
      } else {
        // Count only selected rows that are in filtered set
        return filteredRowIndices.filter(idx => selectedRows.has(idx)).length;
      }
    } else {
      // No filters
      return selectAllRows ? total_rows : selectedRows.size;
    }
  }, [filteredRowIndices, selectAllRows, selectedRows, total_rows]);

  const selectedColumnsCount = selectAllColumns ? columns.length : selectedColumns.size;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Preview: {original_filename}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={`${total_rows} rijen totaal`}
              color="primary"
              size="small"
            />
            <Chip
              label={`${preview_rows} rijen in preview`}
              color="info"
              size="small"
            />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          {/* Show alignment warnings if present */}
          {alignment_warnings && alignment_warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Kolomverschuiving Gedetecteerd</AlertTitle>
              {alignment_warnings.map((warning, idx) => (
                <Typography key={idx} variant="body2">
                  {warning.message}
                </Typography>
              ))}
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Tip:</strong> Controleer of de kolommen in je Excel bestand correct zijn uitgelijnd. 
                Als kolommen zijn verschoven, kunnen records niet correct worden geïmporteerd.
              </Typography>
            </Alert>
          )}
          
          <Alert severity="info" sx={{ mb: 2 }}>
            Selecteer de rijen en kolommen die je wilt importeren. Je kunt alle rijen/kolommen selecteren of specifieke items kiezen.
          </Alert>

          {/* Column Selection */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Kolommen selecteren ({selectedColumnsCount} van {columns.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Zoek kolommen">
                  <TextField
                    size="small"
                    placeholder="Zoek kolommen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchTerm('')}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ width: 250 }}
                  />
                </Tooltip>
                <Button
                  size="small"
                  startIcon={selectAllColumns ? <DeselectIcon /> : <SelectAllIcon />}
                  onClick={handleSelectAllColumns}
                >
                  {selectAllColumns ? 'Deselecteer Alles' : 'Selecteer Alles'}
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 150, overflow: 'auto' }}>
              {filteredColumns.map((column) => {
                const isSelected = selectAllColumns || selectedColumns.has(column);
                return (
                  <Chip
                    key={column}
                    label={column}
                    onClick={() => handleSelectColumn(column)}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    size="small"
                    icon={isSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                  />
                );
              })}
            </Box>
          </Paper>

          {/* Row Selection and Filters */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Rijen selecteren ({selectedRowsCount} van {filteredRowIndices !== null ? filteredRowIndices.length : total_rows}
                {filteredRowIndices !== null && ` (gefilterd van ${total_rows} totaal)`})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Tooltip title="Zoek in records">
                  <TextField
                    size="small"
                    placeholder="Zoek in records..."
                    value={recordSearchTerm}
                    onChange={(e) => {
                      setRecordSearchTerm(e.target.value);
                      setPage(0); // Reset to first page when searching
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: recordSearchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => {
                            setRecordSearchTerm('');
                            setPage(0);
                          }}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ width: 250 }}
                  />
                </Tooltip>
                <Button
                  size="small"
                  startIcon={selectAllRows ? <DeselectIcon /> : <SelectAllIcon />}
                  onClick={handleSelectAllRows}
                >
                  {selectAllRows ? 'Deselecteer Alles' : 'Selecteer Alles'}
                </Button>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {filteredRowIndices !== null 
                ? (selectAllRows
                    ? `Alle ${filteredRowIndices.length} gefilterde rijen worden geïmporteerd`
                    : `${selectedRowsCount} specifieke gefilterde rijen worden geïmporteerd`)
                : (selectAllRows
                    ? `Alle ${total_rows} rijen worden geïmporteerd`
                    : `${selectedRows.size} specifieke rijen worden geïmporteerd`)}
              {recordSearchTerm && recordSearchTerm.trim() && (
                <Chip 
                  label={`Zoek: "${recordSearchTerm}"`} 
                  size="small" 
                  color="info" 
                  sx={{ ml: 1 }}
                  onDelete={() => {
                    setRecordSearchTerm('');
                    setPage(0);
                  }}
                />
              )}
            </Typography>
          </Paper>

          {/* Test Run / Validation Section */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Data Validatie
              </Typography>
              <Button
                variant="outlined"
                color="info"
                size="medium"
                onClick={runValidation}
                disabled={validating || selectedRowsCount === 0 || selectedColumnsCount === 0}
                startIcon={validating ? <CircularProgress size={20} /> : <PlayArrowIcon />}
              >
                {validating ? 'Valideren...' : 'Test Run'}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Valideer de geselecteerde data op fouten en waarschuwingen voordat je importeert
            </Typography>
            {!validationRun && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Verplicht:</strong> Voer eerst een validatie uit (Test Run) voordat je kunt importeren.
                </Typography>
              </Alert>
            )}
            {validationRun && validationResults && validationResults.summary.totalErrors > 0 && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Import geblokkeerd:</strong> Er zijn {validationResults.summary.totalErrors} validatiefout(en) gevonden. Los deze eerst op voordat je importeert.
                </Typography>
              </Alert>
            )}

            {showValidationResults && validationResults && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Chip
                    icon={<ErrorIcon />}
                    label={`${validationResults.summary.totalErrors} Fouten`}
                    color={validationResults.summary.totalErrors > 0 ? 'error' : 'default'}
                    variant="outlined"
                  />
                  <Chip
                    icon={<WarningIcon />}
                    label={`${validationResults.summary.totalWarnings} Waarschuwingen`}
                    color={validationResults.summary.totalWarnings > 0 ? 'warning' : 'default'}
                    variant="outlined"
                  />
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${validationResults.summary.rowsWithErrors.size} Rijen met fouten`}
                    color={validationResults.summary.rowsWithErrors.size > 0 ? 'error' : 'success'}
                    variant="outlined"
                  />
                </Box>

                {validationResults.summary.totalErrors === 0 && validationResults.summary.totalWarnings === 0 ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Geen validatiefouten gevonden! De data is klaar om te importeren.
                  </Alert>
                ) : (
                  <>
                    {validationResults.errors.length > 0 && (
                      <Accordion defaultExpanded sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ErrorIcon color="error" />
                            <Typography variant="subtitle2" fontWeight="bold">
                              Fouten ({validationResults.errors.length})
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Rij</TableCell>
                                  <TableCell>Veld</TableCell>
                                  <TableCell>Waarde</TableCell>
                                  <TableCell>Probleem</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {validationResults.errors.slice(0, 50).map((error, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{error.row}</TableCell>
                                    <TableCell>{error.field}</TableCell>
                                    <TableCell>
                                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                        {String(error.value || '')}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip label={error.message} color="error" size="small" />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {validationResults.errors.length > 50 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              ... en {validationResults.errors.length - 50} meer fouten
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {validationResults.warnings.length > 0 && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningIcon color="warning" />
                            <Typography variant="subtitle2" fontWeight="bold">
                              Waarschuwingen ({validationResults.warnings.length})
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Rij</TableCell>
                                  <TableCell>Veld</TableCell>
                                  <TableCell>Probleem</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {validationResults.warnings.slice(0, 50).map((warning, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{warning.row}</TableCell>
                                    <TableCell>{warning.field}</TableCell>
                                    <TableCell>
                                      <Chip label={warning.message} color="warning" size="small" />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {validationResults.warnings.length > 50 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              ... en {validationResults.warnings.length - 50} meer waarschuwingen
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </>
                )}
              </Box>
            )}
          </Paper>

          {/* Auto-claim option - always required */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Auto-claim alle records voor een gebruiker
            </Typography>
            <FormControl fullWidth required>
              <InputLabel id="claim-user-select-label">Selecteer Gebruiker *</InputLabel>
              <Select
                labelId="claim-user-select-label"
                value={claimForUser}
                label="Selecteer Gebruiker *"
                onChange={(e) => setClaimForUser(e.target.value)}
                disabled={loadingUsers}
                required
                error={!claimForUser}
              >
                {loadingUsers ? (
                  <MenuItem disabled>Gebruikers laden...</MenuItem>
                ) : users.length === 0 ? (
                  <MenuItem disabled>Geen gebruikers beschikbaar</MenuItem>
                ) : (
                  users.map(user => (
                    <MenuItem key={user.username} value={user.username}>
                      {user.display_name || user.username}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Paper>

          {/* Filter Section */}
          {(columns.includes('Filter1') || columns.includes('Filter2') || columns.includes('Filter3')) && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Zoek Filters
                </Typography>
                {loadingFilters && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Filters laden...
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {columns.includes('Filter1') && (
                  <TextField
                    size="small"
                    label="Filter Filter1"
                    placeholder="Zoek in Filter1..."
                    value={filter1}
                    onChange={(e) => setFilter1(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: filter1 && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setFilter1('')}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ minWidth: 200 }}
                  />
                )}
                {columns.includes('Filter2') && (
                  <TextField
                    size="small"
                    label="Filter Filter2"
                    placeholder="Zoek in Filter2..."
                    value={filter2}
                    onChange={(e) => setFilter2(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: filter2 && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setFilter2('')}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ minWidth: 200 }}
                  />
                )}
                {columns.includes('Filter3') && (
                  <TextField
                    size="small"
                    label="Filter Filter3"
                    placeholder="Zoek in Filter3..."
                    value={filter3}
                    onChange={(e) => setFilter3(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: filter3 && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setFilter3('')}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ minWidth: 200 }}
                  />
                )}
                {(filter1 || filter2 || filter3) && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={() => {
                      setFilter1('');
                      setFilter2('');
                      setFilter3('');
                    }}
                  >
                    Wis Filters
                  </Button>
                )}
              </Box>
              {(filter1 || filter2 || filter3) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {loadingFilters ? (
                    'Filters laden...'
                  ) : filteredDataFromServer !== null ? (
                    `${filteredData.length} van ${total_rows} rijen getoond`
                  ) : (
                    `${data.length} van ${total_rows} rijen getoond (eerste ${preview_rows} rijen)`
                  )}
                </Typography>
              )}
            </Paper>
          )}

          {/* Data Preview Table */}
          <Paper sx={{ mt: 2 }}>
            <TableContainer sx={{ maxHeight: 400, overflowX: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ width: 50, position: 'sticky', left: 0, zIndex: 10, backgroundColor: 'background.paper' }}>
                      <Checkbox
                        checked={selectAllRows}
                        indeterminate={
                          (filteredRowIndices !== null || (recordSearchTerm && recordSearchTerm.trim()))
                            ? (() => {
                                const hasFilters = filteredRowIndices !== null;
                                if (hasFilters) {
                                  const selectedFiltered = filteredRowIndices.filter(idx => selectedRows.has(idx));
                                  return selectedFiltered.length > 0 && selectedFiltered.length < filteredRowIndices.length;
                                }
                                return false;
                              })()
                            : (selectedRows.size > 0 && selectedRows.size < data.length)
                        }
                        onChange={handleSelectAllRows}
                      />
                    </TableCell>
                    {columns
                      .filter(col => selectAllColumns || selectedColumns.has(col))
                      .map((column) => (
                        <TableCell key={column} sx={{ minWidth: 150 }}>
                          <Tooltip title={column}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {column}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row, idx) => {
                    // Determine the actual index to use for selection
                    // When filters are active, use original index; otherwise use display index
                    let actualIndex;
                    if (filteredDataFromServer !== null && row._original_index !== undefined) {
                      // Filters are active - use original index
                      actualIndex = row._original_index;
                    } else if (recordSearchTerm && recordSearchTerm.trim() && row._original_index === undefined) {
                      // Record search is active but no _original_index - find it in original data
                      const foundIdx = data.findIndex(r => r === row);
                      actualIndex = foundIdx >= 0 ? foundIdx : page * rowsPerPage + idx;
                    } else {
                      // No filters - use display index (page offset + row index)
                      actualIndex = page * rowsPerPage + idx;
                    }
                    
                    // Check if this row is selected
                    // When filters are active, selectedRows contains original indices
                    // When no filters, selectedRows contains display indices
                    const isSelected = selectAllRows || selectedRows.has(actualIndex);
                    
                    return (
                      <TableRow key={`${actualIndex}-${idx}`} hover>
                        <TableCell padding="checkbox" sx={{ position: 'sticky', left: 0, zIndex: 9, backgroundColor: 'background.paper' }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectRow(actualIndex)}
                          />
                        </TableCell>
                        {columns
                          .filter(col => selectAllColumns || selectedColumns.has(col))
                          .map((column) => (
                            <TableCell key={column} sx={{ minWidth: 150 }}>
                              <Tooltip title={String(row[column] || '')}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {String(row[column] || '')}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Pagina {page + 1} van {Math.ceil(filteredData.length / rowsPerPage)} 
                {((filter1 || filter2 || filter3) || (recordSearchTerm && recordSearchTerm.trim())) && ` (${filteredData.length} van ${data.length} rijen)`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => setPage(0)}
                  disabled={page === 0}
                >
                  Eerste
                </Button>
                <Button
                  size="small"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Vorige
                </Button>
                <Button
                  size="small"
                  onClick={() => setPage(p => Math.min(Math.ceil(filteredData.length / rowsPerPage) - 1, p + 1))}
                  disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
                >
                  Volgende
                </Button>
                <Button
                  size="small"
                  onClick={() => setPage(Math.ceil(filteredData.length / rowsPerPage) - 1)}
                  disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
                >
                  Laatste
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annuleren
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          color="primary"
          disabled={
            loading || 
            !claimForUser || 
            selectedRowsCount === 0 || 
            selectedColumnsCount === 0 ||
            !validationRun ||
            (validationResults && validationResults.summary.totalErrors > 0)
          }
          startIcon={loading ? <CircularProgress size={20} /> : null}
          title={
            !claimForUser
              ? 'Selecteer een gebruiker om door te gaan'
              : !validationRun 
                ? 'Voer eerst een validatie uit (Test Run) voordat je importeert'
                : (validationResults && validationResults.summary.totalErrors > 0)
                  ? `Er zijn ${validationResults.summary.totalErrors} validatiefout(en). Los deze eerst op voordat je importeert.`
                  : undefined
          }
        >
          {loading ? 'Importeren...' : `Importeren (${selectedRowsCount} rijen, ${selectedColumnsCount} kolommen)`}
        </Button>
      </DialogActions>

      {/* Error Dialog */}
      <Dialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon color="error" />
            <Typography variant="h6">Import Mislukt</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {importError || 'Er is een onbekende fout opgetreden bij het importeren van de data.'}
          </Alert>
          
          {/* Show specific error details if available */}
          {importErrorDetails && (importErrorDetails.problematic_record || importErrorDetails.problematic_field || importErrorDetails.problematic_value) && (
            <Paper sx={{ mb: 2, p: 2, bgcolor: 'error.light', border: '1px solid', borderColor: 'error.main' }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold', color: 'error.dark' }}>
                Specifieke foutdetails:
              </Typography>
              {importErrorDetails.problematic_record && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', display: 'inline' }}>
                    Record: 
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'inline', ml: 0.5 }}>
                    {importErrorDetails.problematic_record}
                  </Typography>
                </Box>
              )}
              {importErrorDetails.problematic_field && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', display: 'inline' }}>
                    Veld: 
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'inline', ml: 0.5 }}>
                    {importErrorDetails.problematic_field}
                  </Typography>
                </Box>
              )}
              {importErrorDetails.problematic_value && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', display: 'inline' }}>
                    Problematische waarde: 
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'inline', ml: 0.5, fontFamily: 'monospace', bgcolor: 'background.paper', px: 0.5, borderRadius: 0.5 }}>
                    "{importErrorDetails.problematic_value}"
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Tips om dit op te lossen:</strong>
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 3, mb: 0 }}>
            <li>
              <Typography variant="body2" color="text.secondary">
                Gebruik de "Test Run" knop om fouten te vinden voordat je importeert
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                Controleer of numerieke velden (huisnummer, postcode, etc.) alleen cijfers bevatten
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                Zorg ervoor dat telefoonnummers in het juiste veld staan en niet in numerieke velden
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                Controleer of alle verplichte velden zijn ingevuld
              </Typography>
            </li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorDialog(false)} variant="contained" color="primary">
            Sluiten
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default FilePreview;

